import { IGridRequestBody, IGridResponseBody } from 'mt-ui-lib/interfaces';

export function gridFilter(body: IGridRequestBody, field: string): unknown {
  return body.complexFilters
    ?.flatMap((group) => group.filters ?? [])
    .find((filter) => filter.field === field)?.value;
}

function matches(
  actual: unknown,
  expected: unknown,
  criteria = 'like',
): boolean {
  const value = String(actual ?? '');
  const query = String(expected ?? '');
  const left = value.toLocaleLowerCase(),
    right = query.toLocaleLowerCase();
  switch (criteria) {
    case 'isNull':
      return actual == null || value === '';
    case 'isNotNull':
      return actual != null && value !== '';
    case 'equals':
      return value === query;
    case 'EQUALS_IGNORE_CASE':
      return left === right;
    case 'EQUALS_OR_NULL':
      return !value || value === query;
    case 'notEquals':
      return value !== query;
    case 'like':
      return left.includes(right);
    case 'notLike':
      return !left.includes(right);
    case 'startFrom':
      return left.startsWith(right);
    case 'notStartFrom':
      return !left.startsWith(right);
    case 'endTo':
      return left.endsWith(right);
    case 'notEndTo':
      return !left.endsWith(right);
    case 'in':
      return (Array.isArray(expected) ? expected : [expected])
        .map(String)
        .includes(value);
    case 'notIn':
      return !matches(actual, expected, 'in');
    case 'inOrNull':
      return !value || matches(actual, expected, 'in');
    case 'greaterThan':
      return Number(actual) > Number(expected);
    case 'greaterOrEquals':
      return Number(actual) >= Number(expected);
    case 'lessThan':
      return Number(actual) < Number(expected);
    case 'lessOrEquals':
      return Number(actual) <= Number(expected);
    default:
      throw new Error('Критерий фильтра не поддерживается');
  }
}

/** Paging a complete, bounded settings collection, never a page of server results. */
export function settingsGridPage<T extends object>(
  rows: T[],
  body: IGridRequestBody,
): IGridResponseBody<T> {
  let items = rows.filter((row) => {
    const search = body.searchString?.toLocaleLowerCase();
    if (
      search &&
      !Object.values(row).some((value) =>
        String(value ?? '')
          .toLocaleLowerCase()
          .includes(search),
      )
    )
      return false;
    return (body.complexFilters ?? []).every((group) => {
      const filters = (group.filters ?? []).filter(
        (filter) =>
          (filter.value != null && filter.value !== '') ||
          ['isNull', 'isNotNull'].includes(filter.criteria),
      );
      const check = (filter: (typeof filters)[number]) =>
        matches(row[filter.field], filter.value, filter.criteria);
      return group.operator === 'or'
        ? !filters.length || filters.some(check)
        : filters.every(check);
    });
  });
  for (const sorting of [...(body.sortings ?? [])].sort(
    (a, b) => b.index - a.index,
  )) {
    items = [...items].sort(
      (a, b) =>
        String(a[sorting.field] ?? '').localeCompare(
          String(b[sorting.field] ?? ''),
          'ru',
          { numeric: true },
        ) * (sorting.direction === 'desc' ? -1 : 1),
    );
  }
  const pageNumber = Math.max(1, body.page?.pageNumber ?? 1);
  const pageSize = Math.min(100, Math.max(1, body.page?.pageSize ?? 50));
  return {
    items: items.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    pageNumber,
    pageSize,
    totalElements: items.length,
    totalPages: Math.ceil(items.length / pageSize),
  };
}
